import { Repeat, MessageCircle, Share } from 'lucide-react';
import type { RepostData, ExtendedPost } from '../../types/index';
import { formatDate } from '../../utils/formatDate';
import { formatNumber } from '../../utils/formatNumber';

interface RepostCardProps {
  repostData: RepostData;
  originalPost?: ExtendedPost;
  onOriginalPostClick?: () => void;
  onRepost?: () => void;
  onShare?: () => void;
}

export default function RepostCard({ 
  repostData, 
  originalPost, 
  onOriginalPostClick,
  onRepost,
  onShare 
}: RepostCardProps) {
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      {/* Repost header */}
      <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <Repeat className="h-4 w-4 text-green-600 dark:text-green-400" />
        <span className="text-sm text-gray-600 dark:text-gray-400">
          Reposted from <span className="font-medium text-gray-900 dark:text-gray-100">@{repostData.originalUsername}</span>
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
          {formatDate(repostData.createdAt)}
        </span>
      </div>

      {/* Repost caption (for quote reposts) */}
      {repostData.repostType === 'quote' && repostData.repostCaption && (
        <div className="p-3 border-b border-gray-200 dark:border-gray-700">
          <p className="text-gray-900 dark:text-gray-100">
            {repostData.repostCaption}
          </p>
        </div>
      )}

      {/* Original post preview */}
      {originalPost && (
        <div 
          onClick={onOriginalPostClick}
          className="p-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer"
        >
          {/* Original post header */}
          <div className="flex items-center gap-2 mb-2">
            <img
              src={originalPost.users.avatar_url}
              alt={originalPost.users.username}
              className="w-6 h-6 rounded-full"
            />
            <span className="font-medium text-gray-900 dark:text-gray-100">
              @{originalPost.users.username}
            </span>
            {originalPost.users.verified && (
              <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            )}
            <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
              {formatDate(originalPost.created_at)}
            </span>
          </div>

          {/* Original post content */}
          {originalPost.content && (
            <p className="text-gray-900 dark:text-gray-100 mb-2 line-clamp-3">
              {originalPost.content}
            </p>
          )}

          {/* Original post media preview */}
          {originalPost.media_url && (
            <div className="relative rounded-lg overflow-hidden mb-2">
              {originalPost.type === 'video' ? (
                <video
                  src={originalPost.media_url}
                  className="w-full h-32 object-cover"
                  muted
                />
              ) : (
                <img
                  src={originalPost.media_url}
                  alt="Post media"
                  className="w-full h-32 object-cover"
                />
              )}
            </div>
          )}

          {/* Original post stats */}
          <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
            <div className="flex items-center gap-1">
              <MessageCircle className="h-3 w-3" />
              {formatNumber(originalPost.comment_count || 0)}
            </div>
            <div className="flex items-center gap-1">
              <Repeat className="h-3 w-3" />
              {formatNumber(originalPost.reshare_count || 0)}
            </div>
            <div className="flex items-center gap-1">
              <span>❤️</span>
              {formatNumber(originalPost.like_count || originalPost.likes || 0)}
            </div>
          </div>
        </div>
      )}

      {/* Repost actions */}
      <div className="flex items-center justify-between p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <button
          onClick={onRepost}
          className="flex items-center gap-2 px-3 py-1 rounded-md hover:bg-green-100 dark:hover:bg-green-900 text-green-600 dark:text-green-400 transition-colors"
        >
          <Repeat className="h-4 w-4" />
          <span className="text-sm font-medium">Repost</span>
        </button>

        <button
          onClick={onShare}
          className="flex items-center gap-2 px-3 py-1 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900 text-blue-600 dark:text-blue-400 transition-colors"
        >
          <Share className="h-4 w-4" />
          <span className="text-sm font-medium">Share</span>
        </button>
      </div>
    </div>
  );
}
